import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Phone, ShieldCheck, MessageSquare } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

export default function VerifyPhone() {
    const { user, updateUser } = useAuth();
    const navigate = useNavigate();
    const [phone, setPhone] = useState(user?.phone || "+679");
    const [step, setStep] = useState(1);
    const [code, setCode] = useState("");
    const [devCode, setDevCode] = useState("");
    const [loading, setLoading] = useState(false);

    const sendCode = async () => {
        setLoading(true);
        try {
            const { data } = await api.post("/auth/phone/start", { phone });
            setDevCode(data.dev_code || "");
            setStep(2);
            toast.success("Verification code sent (mocked).");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    const confirmCode = async () => {
        setLoading(true);
        try {
            const { data } = await api.post("/auth/phone/confirm", { code });
            updateUser(data.user);
            toast.success("Phone verified! Welcome to the platform.");
            navigate("/dashboard");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    if (user?.phone_verified) {
        return (
            <div className="max-w-2xl mx-auto px-4 py-20 text-center">
                <ShieldCheck className="h-16 w-16 mx-auto text-fiji-green mb-4" />
                <h1 className="font-display text-3xl font-bold mb-3">Phone already verified</h1>
                <p className="text-muted-foreground font-app mb-6">You're all set.</p>
                <Button onClick={() => navigate("/dashboard")} className="bg-ocean-green text-white">Go to dashboard</Button>
            </div>
        );
    }

    return (
        <div className="max-w-xl mx-auto px-4 py-16">
            <Card className="p-8">
                <Phone className="h-10 w-10 text-coral mb-4" />
                <h1 className="font-display text-3xl font-bold mb-2">Verify your phone</h1>
                <p className="text-sm text-muted-foreground font-app mb-6">
                    For security, every Bula Real Estate Fiji member verifies a Fijian or international phone number.
                    <span className="block mt-1 text-coral">Note: SMS is currently MOCKED — your code will appear on this page.</span>
                </p>

                {step === 1 && (
                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="phone">Phone number</Label>
                            <Input id="phone" data-testid="verify-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5" />
                        </div>
                        <Button onClick={sendCode} disabled={loading} data-testid="send-otp-btn" className="w-full bg-ocean-green text-white">
                            <MessageSquare className="h-4 w-4 mr-2" /> {loading ? "Sending…" : "Send verification code"}
                        </Button>
                    </div>
                )}

                {step === 2 && (
                    <div className="space-y-4">
                        {devCode && (
                            <div className="p-4 bg-coral/10 border border-coral/30 rounded-md font-app text-sm">
                                <div className="text-xs uppercase tracking-wider text-coral font-semibold mb-1">Dev OTP (mocked)</div>
                                <div className="font-mono text-2xl font-bold tracking-widest text-ocean dark:text-turquoise" data-testid="dev-otp-display">{devCode}</div>
                            </div>
                        )}
                        <div>
                            <Label htmlFor="code">6-digit code</Label>
                            <Input id="code" data-testid="otp-code-input" value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} className="mt-1.5 font-mono text-lg tracking-widest" />
                        </div>
                        <Button onClick={confirmCode} disabled={loading} data-testid="confirm-otp-btn" className="w-full bg-coral text-white hover:bg-coral-deep">
                            {loading ? "Verifying…" : "Verify code"}
                        </Button>
                        <Button variant="ghost" onClick={() => setStep(1)} className="w-full font-app text-sm">Change number</Button>
                    </div>
                )}
            </Card>
        </div>
    );
}
